import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { cn } from '../lib/utils.js';
import { isRtl } from '../lib/rtl.js';
import { focusRing, insetFocusRing } from '../lib/focus.js';
import {
  addDays,
  addMonths,
  clampDayKey,
  endOfWeek,
  monthGrid,
  parseDayKey,
  startOfWeek,
  withinBounds,
  type DayKey,
} from '../lib/dayKey.js';
import type { DateFieldLabels } from './dateLabels.js';

/* ===========================================================================
 * ONE MONTH, DRAWN — the grid both date controls share.
 *
 * MONDAY FIRST, and Fr/Sa muted. The attendance calendar puts Monday in the
 * first column, so this does too: a picker that started the week on Sunday
 * would place 14 September 2026 in a different column from the grid directly
 * beside it, which is design-rules §3.2 (one fact, one reading) at its most
 * literal. Friday and Saturday carry the lighter ink because they are the UAE
 * weekend — the tone is a hint, and the WORD in the heading is what actually
 * carries it, per §1's rule that colour never means anything alone.
 *
 * TODAY IS A RING, NEVER A FILL. This is the one rule in the component that is
 * not negotiable on taste. Today and the selected day coincide on exactly one
 * day, and if both were fills the reader would have no way to tell whether the
 * highlighted cell is where they are or what they chose — on the single day
 * the distinction matters most. An inset `--primary-border` hairline sits under
 * a selected fill and stays visible beside it.
 *
 * NOTHING HERE READS A CLOCK. `today` is a day key handed in by the caller; it
 * is optional, and its absence draws no ring rather than guessing. See
 * `lib/dayKey.ts` and docs/time-and-dates.md.
 * ======================================================================== */

export interface CalendarMonthProps {
  /** Any day inside the month to draw. */
  cursor: DayKey;
  /** The company's today, or null. Never derived here. */
  today?: DayKey | null;
  /** Filled cells. One key for a single date; both ends for a range. */
  selectedStart?: DayKey | null;
  selectedEnd?: DayKey | null;
  /** Draw the continuous band between the ends. */
  band?: boolean;
  min?: DayKey | null;
  max?: DayKey | null;
  /** The roving-tabindex day. Owned by the parent so two months share one. */
  focusedKey: DayKey;
  onFocusedKeyChange: (key: DayKey) => void;
  onSelect: (key: DayKey) => void;
  /** Whether this month's grid holds the DOM focus. False on a sibling month. */
  ownsFocus: boolean;
  /** Set once the reader has moved focus into the grid, so it does not steal it. */
  focusActive: boolean;
  onEscape: () => void;
  labels: DateFieldLabels;
  /** Rendered only when given — the leading month draws ‹, the trailing ›. */
  onPrevMonth?: () => void;
  onNextMonth?: () => void;
}

/* THE CELL.
 *
 * 36px square at `rounded-xs`, on `text-figure` — the body-sized numeral step:
 * a day number is a figure in a grid, so it gets Archivo and `tabular-nums`
 * like every other figure in this product, at the size the cell actually has
 * room for. That step exists BECAUSE of this cell (and TimeField's step list):
 * the scale's three `metric*` numeral steps are 26–40px KPI sizes, and there
 * was nothing between them and body prose. It is declared in
 * `../../tailwind-type.ts` and takes its family and figures from the plugin in
 * `../../tailwind-preset.ts`; it introduces no new value, being `--text-copy`
 * at weight 500.
 *
 * Do not write `tabular-nums` here instead — the step carries it, and a
 * hand-written one is what `check:designtokens` bans as `raw-tabular`. */
const DAY_CELL =
  'flex size-9 items-center justify-center text-figure transition-colors duration-fast ease-standard';

/** Fr and Sa — indices 4 and 5 in a Monday-first week. */
const WEEKEND = new Set([4, 5]);

export function CalendarMonth({
  cursor,
  today,
  selectedStart,
  selectedEnd,
  band = false,
  min,
  max,
  focusedKey,
  onFocusedKeyChange,
  onSelect,
  ownsFocus,
  focusActive,
  onEscape,
  labels,
  onPrevMonth,
  onNextMonth,
}: CalendarMonthProps): JSX.Element {
  const gridRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLButtonElement>(null);
  const parsed = parseDayKey(cursor);
  const year = parsed?.year ?? 0;
  const month = parsed?.month ?? 1;
  const cells = parsed ? monthGrid(year, month) : [];
  const monthLabel = `${labels.monthNames[month - 1] ?? ''} ${year}`;

  useEffect(() => {
    if (ownsFocus && focusActive) focusRef.current?.focus();
  }, [ownsFocus, focusActive, focusedKey]);

  function move(next: DayKey): void {
    /* Clamped rather than blocked. Arrowing past the boundary lands the reader
       ON the last legal day instead of leaving the caret stuck with nothing on
       screen having changed — a keypress that does nothing is indistinguishable
       from a keyboard that is not working. */
    onFocusedKeyChange(clampDayKey(next, min, max));
  }

  function onKeyDown(e: ReactKeyboardEvent<HTMLDivElement>): void {
    /* ARROWS FOLLOW THE WRITING DIRECTION. Under Arabic the grid mirrors, so
       the cell to the reader's LEFT is the NEXT day; a hard-coded
       ArrowLeft = -1 would run the caret backwards through a calendar that is
       visibly advancing. Read off the grid's own resolved `dir`, not the
       document's — see lib/rtl.ts. */
    const back = isRtl(gridRef.current) ? 1 : -1;
    switch (e.key) {
      case 'ArrowLeft':
        move(addDays(focusedKey, back));
        break;
      case 'ArrowRight':
        move(addDays(focusedKey, -back));
        break;
      case 'ArrowUp':
        move(addDays(focusedKey, -7));
        break;
      case 'ArrowDown':
        move(addDays(focusedKey, 7));
        break;
      case 'PageUp':
        move(addMonths(focusedKey, -1));
        break;
      case 'PageDown':
        move(addMonths(focusedKey, 1));
        break;
      case 'Home':
        move(startOfWeek(focusedKey));
        break;
      case 'End':
        move(endOfWeek(focusedKey));
        break;
      case 'Enter':
      case ' ':
        if (withinBounds(focusedKey, min, max)) onSelect(focusedKey);
        break;
      case 'Escape':
        onEscape();
        return;
      default:
        return;
    }
    // Every branch above either moved the caret or committed, and both would
    // otherwise also scroll the panel or submit the form around it.
    e.preventDefault();
    e.stopPropagation();
  }

  return (
    <div className="flex flex-col gap-element">
      <div className="flex items-center justify-between gap-element">
        {onPrevMonth ? (
          <MonthStep label={labels.previousMonth} onClick={onPrevMonth} direction="prev" />
        ) : (
          <span className="size-8" aria-hidden />
        )}
        <div className="font-display text-copy font-semibold text-foreground">{monthLabel}</div>
        {onNextMonth ? (
          <MonthStep label={labels.nextMonth} onClick={onNextMonth} direction="next" />
        ) : (
          <span className="size-8" aria-hidden />
        )}
      </div>

      <div
        ref={gridRef}
        role="grid"
        aria-label={monthLabel}
        onKeyDown={onKeyDown}
        className="flex flex-col"
      >
        <div role="row" className="flex">
          {labels.weekdayShort.map((short, i) => (
            <div
              key={short}
              role="columnheader"
              aria-label={labels.weekdayLong[i]}
              className={cn(
                'flex size-9 items-center justify-center text-label',
                WEEKEND.has(i) ? 'text-foreground-tertiary' : 'text-muted-foreground'
              )}
            >
              {short}
            </div>
          ))}
        </div>
        {[0, 1, 2, 3, 4, 5].map((week) => (
          <div role="row" className="flex" key={week}>
            {cells.slice(week * 7, week * 7 + 7).map((cell) => {
              const enabled = withinBounds(cell.key, min, max);
              const isEnd = cell.key === selectedStart || cell.key === selectedEnd;
              const inBand =
                band &&
                !!selectedStart &&
                !!selectedEnd &&
                cell.key > selectedStart &&
                cell.key < selectedEnd;
              const isToday = !!today && cell.key === today;
              const isFocused = cell.key === focusedKey;
              return (
                <div role="gridcell" key={cell.key} className="flex">
                  <button
                    ref={ownsFocus && isFocused ? focusRef : undefined}
                    type="button"
                    // A REAL BUTTON with `aria-pressed`, not a div with a class.
                    // The pressed state is what a screen reader reads back as
                    // "selected"; a coloured background says nothing at all.
                    aria-pressed={isEnd}
                    aria-current={isToday ? 'date' : undefined}
                    aria-label={cell.key}
                    disabled={!enabled}
                    /* Roving tabindex: one stop for the whole grid. Forty-two
                       tab stops in a popover is a keyboard trap wearing a
                       calendar's clothes. A DISABLED day is out of the tab
                       order by the attribute alone. */
                    tabIndex={isFocused ? 0 : -1}
                    onClick={() => onSelect(cell.key)}
                    onFocus={() => {
                      if (cell.key !== focusedKey) onFocusedKeyChange(cell.key);
                    }}
                    className={cn(
                      DAY_CELL,
                      // The band's corners: rounded at the ends, square in the
                      // middle, so the run reads as one object. `-s-`/`-e-` are
                      // logical, so the band mirrors under Arabic on its own.
                      inBand ? 'rounded-none' : 'rounded-xs',
                      band && isEnd && selectedStart !== selectedEnd
                        ? cell.key === selectedStart
                          ? 'rounded-e-none'
                          : 'rounded-s-none'
                        : undefined,
                      !enabled && 'cursor-not-allowed text-foreground-disabled',
                      enabled && cell.outside && 'text-foreground-disabled',
                      enabled && !cell.outside && 'text-foreground',
                      enabled && !isEnd && 'hover:bg-muted',
                      inBand && 'bg-primary-tint text-primary-deep',
                      isEnd && 'bg-primary text-primary-foreground',
                      // TODAY. A ring, inset so it sits INSIDE the 36px square
                      // and can be drawn under a selected fill rather than
                      // replaced by it.
                      isToday && 'ring-1 ring-inset ring-primary-border',
                      insetFocusRing
                    )}
                  >
                    {cell.day}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthStep({
  label,
  onClick,
  direction,
}: {
  label: string;
  onClick: () => void;
  direction: 'prev' | 'next';
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        'flex size-8 flex-none items-center justify-center rounded-control border border-border',
        'bg-surface text-foreground transition-colors duration-fast ease-standard hover:bg-muted',
        focusRing
      )}
    >
      {/* `rtl:-scale-x-100` is this codebase's chevron idiom (MonthStepper,
          AttendanceDayStepper, the MyAttendance/AuditLog disclosures): the glyph
          is mirrored rather than swapped, so "previous" always points at the
          inline-start edge whichever way the page runs. */}
      <svg
        viewBox="0 0 16 16"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="rtl:-scale-x-100"
      >
        <path d={direction === 'prev' ? 'M10 3.5 5.5 8l4.5 4.5' : 'M6 3.5 10.5 8 6 12.5'} />
      </svg>
    </button>
  );
}

/** The month a key belongs to, as the cursor the grid wants. Exported for the
 *  two field components, which shift the cursor when the caret leaves a month. */
export function monthCursorFor(key: DayKey): DayKey {
  const p = parseDayKey(key);
  return p ? `${String(p.year).padStart(4, '0')}-${String(p.month).padStart(2, '0')}-01` : key;
}

/** Whether `key` is drawn inside the month `cursor` names (not an outside cell). */
export function sameMonth(a: DayKey, b: DayKey): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}
